import React, { useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiCompass, FiUsers, FiSettings, FiCheckCircle, FiHome,
  FiTarget, FiFileText, FiClock, FiDollarSign, FiTool,
  FiClipboard, FiShield, FiTrendingUp, FiAward, FiPhoneCall,
} from "react-icons/fi";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" },
  },
};

const fadeLeft = {
  hidden: { opacity: 0, x: -60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const fadeRight = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const staggerContainer = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.12 },
  },
};

const cardStagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1, delayChildren: 0.3 },
  },
};

const processDetails = [
  {
    number: "01",
    title: "Consult & Vision",
    subtitle: "Understanding Your Dream",
    icon: <FiCompass className="w-8 h-8" />,
    description:
      "We begin by getting to know you and your project. This crucial first step ensures we're aligned on your vision, budget, and timeline from the very start.",
    details: [
      {
        icon: <FiPhoneCall className="w-5 h-5" />,
        title: "Initial Consultation",
        text: "Schedule a free consultation to discuss your project goals, requirements, and expectations.",
      },
      {
        icon: <FiTarget className="w-5 h-5" />,
        title: "Goal Setting",
        text: "Define clear objectives, identify must-haves versus nice-to-haves, and establish project priorities.",
      },
      {
        icon: <FiDollarSign className="w-5 h-5" />,
        title: "Budget Analysis",
        text: "Discuss your budget range and provide transparent cost estimates with detailed breakdowns.",
      },
      {
        icon: <FiClock className="w-5 h-5" />,
        title: "Timeline Planning",
        text: "Create a realistic project timeline that aligns with your move-in date or completion needs.",
      },
    ],
  },
  {
    number: "02",
    title: "Design & Planning",
    subtitle: "Bringing Your Vision to Life",
    icon: <FiUsers className="w-8 h-8" />,
    description:
      "Our expert design team transforms your ideas into detailed plans and 3D visualizations, ensuring every detail is perfect before construction begins.",
    details: [
      {
        icon: <FiFileText className="w-5 h-5" />,
        title: "Concept Development",
        text: "Create initial design concepts with multiple options to explore different architectural styles and layouts.",
      },
      {
        icon: <FiTool className="w-5 h-5" />,
        title: "Detailed Planning",
        text: "Develop comprehensive blueprints, structural plans, and technical specifications for all systems.",
      },
      {
        icon: <FiClipboard className="w-5 h-5" />,
        title: "Material Selection",
        text: "Guide you through choosing finishes, fixtures, and materials that match your style and budget.",
      },
      {
        icon: <FiShield className="w-5 h-5" />,
        title: "City Approvals",
        text: "Handle all zoning requirements, architectural reviews, and necessary municipal approvals.",
      },
    ],
  },
  {
    number: "03",
    title: "Permits & Preconstruction",
    subtitle: "Setting the Foundation for Success",
    icon: <FiSettings className="w-8 h-8" />,
    description:
      "We handle all the complex paperwork and coordination required to ensure your project meets all legal requirements and is ready for construction.",
    details: [
      {
        icon: <FiFileText className="w-5 h-5" />,
        title: "Permit Applications",
        text: "Submit and manage all building permits, ensuring compliance with local codes and regulations.",
      },
      {
        icon: <FiClipboard className="w-5 h-5" />,
        title: "Site Preparation",
        text: "Coordinate site surveys, soil testing, utility connections, and necessary demolition work.",
      },
      {
        icon: <FiUsers className="w-5 h-5" />,
        title: "Contractor Coordination",
        text: "Schedule and coordinate all subcontractors, suppliers, and specialized tradespeople.",
      },
      {
        icon: <FiShield className="w-5 h-5" />,
        title: "Pre-Construction Inspection",
        text: "Conduct thorough site inspections and final checks before breaking ground on your project.",
      },
    ],
  },
  {
    number: "04",
    title: "Build Phase",
    subtitle: "Executing with Precision",
    icon: <FiCheckCircle className="w-8 h-8" />,
    description:
      "This is where your vision becomes reality. We execute construction with meticulous attention to detail, maintaining strict quality control throughout.",
    details: [
      {
        icon: <FiTool className="w-5 h-5" />,
        title: "Quality Construction",
        text: "Execute every phase of construction with skilled craftsmen and premium materials.",
      },
      {
        icon: <FiTrendingUp className="w-5 h-5" />,
        title: "Regular Updates",
        text: "Provide weekly progress reports, photos, and site visits to keep you informed every step of the way.",
      },
      {
        icon: <FiShield className="w-5 h-5" />,
        title: "Quality Control",
        text: "Implement rigorous quality checks at every milestone to ensure work meets our high standards.",
      },
      {
        icon: <FiClipboard className="w-5 h-5" />,
        title: "Change Management",
        text: "Handle any modifications professionally with clear documentation and cost transparency.",
      },
    ],
  },
  {
    number: "05",
    title: "Completion & Move-In",
    subtitle: "Delivering Your Dream Home",
    icon: <FiHome className="w-8 h-8" />,
    description:
      "The final stage ensures everything is perfect before you move in. We don't consider the job done until you're completely satisfied.",
    details: [
      {
        icon: <FiCheckCircle className="w-5 h-5" />,
        title: "Final Inspection",
        text: "Conduct comprehensive walkthrough to ensure every detail meets specifications and your expectations.",
      },
      {
        icon: <FiClipboard className="w-5 h-5" />,
        title: "Punch List Completion",
        text: "Address any final touch-ups or adjustments quickly and professionally before handover.",
      },
      {
        icon: <FiAward className="w-5 h-5" />,
        title: "Quality Guarantee",
        text: "Provide warranty documentation and guarantee our workmanship with comprehensive coverage.",
      },
      {
        icon: <FiHome className="w-5 h-5" />,
        title: "Smooth Handoff",
        text: "Complete final documentation, provide maintenance guides, and celebrate your successful project.",
      },
    ],
  },
];

const ProcessDetailPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="bg-[#f8f9fa] min-h-screen">
      <section className="pt-2 pb-8 sm:pt-4 sm:pb-8 md:pt-6 md:pb-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">

          {/* Section Heading */}
          <motion.div
            className="text-center mb-4"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0 }}
          >
            <motion.h2
              variants={fadeUp}
              className="text-3xl font-bold text-[#212529]"
            >
              Process Step Details
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-[#6c757d] mt-1 text-sm sm:text-base"
            >
              A step-by-step breakdown of our construction process.
            </motion.p>
          </motion.div>

          {/* Process Cards */}
          <div className="space-y-6 sm:space-y-8">
            {processDetails.map((process, index) => (

              // ONE whileInView per card — children inherit trigger
              <motion.div
                key={index}
                variants={cardStagger}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                className="bg-white rounded-2xl border-2 border-[#dee2e6] overflow-hidden
                           transition-all duration-500 hover:scale-[1.02]
                           flex flex-col items-center text-center
                           hover:shadow-[0_0_25px_rgba(173,181,189,0.4)]
                           w-full"
              >
                <div className="p-6 sm:p-8 md:p-10 w-full">

                  {/* Header row — slides in from alternating sides */}
                  <motion.div
                    variants={index % 2 === 0 ? fadeLeft : fadeRight}
                    className="flex flex-col md:flex-row items-center md:items-start gap-4 sm:gap-6 w-full"
                  >
                    {/* Number Badge */}
                    <div className="flex-shrink-0">
                      <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#343a40] flex items-center justify-center text-white font-bold text-xl sm:text-2xl shadow-lg">
                        {process.number}
                      </div>
                    </div>

                    {/* Title and Description */}
                    <div className="flex-1 text-center md:text-left">
                      <h2 className="text-[#343a40] text-xl sm:text-2xl md:text-3xl font-bold mb-2">
                        {process.title}
                      </h2>
                      <p className="text-[#6c757d] text-sm sm:text-base font-medium mb-3">
                        {process.subtitle}
                      </p>
                      <p className="text-[#495057] text-sm sm:text-base leading-relaxed">
                        {process.description}
                      </p>
                    </div>

                    {/* Icon */}
                    <div className="hidden md:flex flex-shrink-0">
                      <div className="w-16 h-16 rounded-xl bg-[#343a40] flex items-center justify-center text-white">
                        {process.icon}
                      </div>
                    </div>
                  </motion.div>

                  {/* Detail Cards — fade up after header */}
                  <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 w-full">
                    {process.details.map((detail, idx) => (
                      <motion.div
                        key={idx}
                        variants={fadeUp}
                        className="bg-[#f8f9fa] rounded-xl p-4 sm:p-5 border border-[#dee2e6]
                                   hover:border-[#343a40] transition-all duration-300 hover:shadow-md text-left"
                      >
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-white border border-[#dee2e6] flex items-center justify-center text-[#343a40]">
                            {detail.icon}
                          </div>
                          <div className="flex-1">
                            <h4 className="text-[#212529] font-semibold text-sm sm:text-base mb-1 text-left">
                              {detail.title}
                            </h4>
                            <p className="text-[#6c757d] text-xs sm:text-sm leading-relaxed text-left">
                              {detail.text}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                </div>
              </motion.div>
            ))}
          </div>

        </div>
      </section>
    </div>
  );
};

export default ProcessDetailPage;